// Backend-health store — tracks the LATEST transient GraphQL /
// HTTP error so the app's status bar can surface it in place of a
// toast popup.  Rationale: 400 / 500 / network failures are almost
// always temporary (backend restart, upstream broker blip, network
// blip) and resolve on the next poll — a toast that blocks the
// operator's attention for a self-healing hiccup is worse than
// silence.  Persistent errors still surface, just as a status-bar
// dot instead of a modal-style toast.

import { defineStore } from 'pinia'

/** How the error is classified for display.  Purely user-facing —
 *  internal names (Apollo, GraphQL) are deliberately absent. */
export type BackendErrorKind = 'network' | 'server' | 'client' | 'timeout' | 'unknown'

export interface BackendError {
    /** Caller-supplied context, e.g. "Loading schedules" or
     *  "Refreshing assets".  Shown verbatim to the operator. */
    readonly description: string
    readonly kind: BackendErrorKind
    /** Unix ms of the most recent report. */
    readonly at: number
}

/** How long the latest error stays on the status bar without a
 *  fresh report.  If nothing else has failed for this long the
 *  hiccup has almost certainly resolved and the dot dismisses
 *  itself. */
const AUTOCLEAR_MS = 2 * 60 * 1000

export const useBackendHealthStore = defineStore('backendHealth', {
    state: () => ({
        latest: null as null | BackendError,
        recentCount: 0,
    }),
    getters: {
        /** True while there's a report younger than AUTOCLEAR_MS.
         *  Consumers call this from a template with a reactive time
         *  reference so the badge visibly disappears after the
         *  hiccup ages out. */
        isRecent(state): (now: number) => boolean {
            return (now: number) => {
                if (!state.latest) return false
                return (now - state.latest.at) < AUTOCLEAR_MS
            }
        },
        /** Human-readable one-liner.  No stack traces, no Apollo
         *  wording — just what the operator can act on ("try again
         *  in a moment", "check your connection"). */
        summary(state): string | null {
            const l = state.latest
            if (!l) return null
            switch (l.kind) {
                case 'network': return `${l.description} — server unreachable`
                case 'server':  return `${l.description} — server error (temporary)`
                case 'client':  return `${l.description} — request rejected`
                case 'timeout': return `${l.description} — request timed out`
                default:        return `${l.description} — request failed`
            }
        },
    },
    actions: {
        report(description: string, kind: BackendErrorKind): void {
            this.latest = { description, kind, at: Date.now() }
            this.recentCount++
        },
        clear(): void {
            this.latest = null
            this.recentCount = 0
        },
    },
})

/** Classify a caught exception (Apollo, fetch, generic Error) into
 *  a transient-error kind.  Returns null when the error looks like
 *  a real application error and should NOT be swallowed into the
 *  status bar (e.g. authorization mistake, a validation failure).
 *  Kept next to the store so both errors.ts and the Apollo error
 *  link can share one classifier. */
export function classifyTransientError(e: unknown): BackendErrorKind | null {
    if (!e || typeof e !== 'object') return null
    const err = e as Record<string, any>

    // Apollo `ServerError` / networkError with an HTTP status.
    const status = err.networkError?.statusCode ?? err.statusCode ?? err.status
    if (typeof status === 'number') {
        if (status === 401 || status === 403) return null   // auth is a real problem
        if (status >= 500) return 'server'
        if (status >= 400) return 'client'
    }

    // Apollo `NetworkError` without a status = fetch failure / offline.
    if (err.networkError && !err.networkError.statusCode) return 'network'

    // Aborted requests / manual timeouts.
    if (err.name === 'AbortError') return 'timeout'
    const msg = typeof err.message === 'string' ? err.message : ''
    if (/timed out|timeout/i.test(msg)) return 'timeout'
    if (/Failed to fetch|NetworkError|ERR_NETWORK/i.test(msg)) return 'network'

    return null
}
